import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash, faCheck, faTimes, faEye } from '@fortawesome/free-solid-svg-icons';
import TaskReportModal from './TaskReportModal';
import Select from 'react-select';
import './bookingTasks.css';
import { SkeletonRows } from '../BookingDetails/SectionSkeleton';

export default function BookingTasks({ bookingId, clientId, propertyId }) {
  const [tasks, setTasks] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const [newInstruction, setNewInstruction] = useState('');
  const [newReportRequired, setNewReportRequired] = useState(true);

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedTask, setEditedTask] = useState({
    instruction: '',
    is_report_required: true,
    status: 'pending',
    skipping_reason: '',
  });

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportTask, setReportTask] = useState(null);

  // ✅ Skipping reason VIEW modal (for already skipped tasks)
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipTask, setSkipTask] = useState(null);
  const [skipReason, setSkipReason] = useState('');
  const [skipLoading, setSkipLoading] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);

  // Lightbox for a task's setup photo (opens in-page, not a new tab)
  const [photoModalUrl, setPhotoModalUrl] = useState(null);
  const timerRef = useRef(null);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'skipped', label: 'Skipped' },
  ];

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 36,
      height: 36,
      borderRadius: 10,
      borderColor: state.isFocused ? '#FF5A60' : '#E5E7EB',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(255,90,96,0.15)' : 'none',
      ':hover': { borderColor: '#FF5A60' },
      fontSize: 13,
    }),
    valueContainer: (base) => ({ ...base, height: 36, padding: '0 10px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 36 }),

    // ✅ Only ONE "menu"
    menu: (base) => ({
      ...base,
      borderRadius: 10,
      overflow: 'hidden',
    }),

    option: (base, state) => ({
      ...base,
      fontSize: 13,
      backgroundColor: state.isSelected
        ? 'rgba(255,90,96,0.10)'
        : state.isFocused
          ? '#F9FAFB'
          : '#fff',
      color: '#111827',
    }),

    // ✅ Helps when dropdown is clipped by containers
    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
  };



  // auto-hide feedback after 6 seconds
  useEffect(() => {
    if (!feedbackMessage) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setFeedbackMessage(''), 6000);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [feedbackMessage]);

  const fetchBookingTasks = useCallback(async () => {
    if (!bookingId) return;
    try {
      const res = await axios.get(`/api/tasks/booking/${bookingId}`);
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching booking tasks:', e);
      setTasks([]);
      setFeedbackMessage('Failed to load tasks.');
    }
  }, [bookingId]);

  const ensureTasks = useCallback(async () => {
    if (!bookingId) return;
    try {
      await axios.post(`/api/tasks/booking/${bookingId}/ensure`);
    } catch (e) {
      console.error('Error ensuring booking tasks:', e);
      // not fatal
    }
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId) return;

    (async () => {
      setIsProcessing(true);
      setFeedbackMessage('');
      setInitialLoading(true);

      try {
        await ensureTasks();
        await fetchBookingTasks();
      } finally {
        setIsProcessing(false);
        setInitialLoading(false);
      }
    })();
  }, [bookingId, ensureTasks, fetchBookingTasks]);

  const getSourceLabel = (source) => {
    if (source === 'client') return 'Client';
    if (source === 'property') return 'Property';
    if (source === 'booking') return 'Booking';
    return source || '—';
  };

  const openSourceLink = (source) => {
    if (source === 'client' && clientId) {
      window.open(`/clients/${clientId}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (source === 'property' && propertyId) {
      window.open(`/properties/${propertyId}`, '_blank', 'noopener,noreferrer');
      return;
    }
  };

  const addBookingTask = async () => {
    const instruction = (newInstruction || '').trim();
    if (!instruction) {
      setFeedbackMessage('Please enter a task instruction.');
      return;
    }

    setIsProcessing(true);
    setFeedbackMessage('');

    try {
      await axios.post(`/api/tasks/booking/${bookingId}`, {
        instruction,
        is_report_required: !!newReportRequired,
      });

      setNewInstruction('');
      setNewReportRequired(true);

      await fetchBookingTasks();
      setFeedbackMessage('Booking task added.');
    } catch (e) {
      console.error('Error adding booking task:', e);
      setFeedbackMessage(e.response?.data?.error || 'Failed to add task.');
    } finally {
      setIsProcessing(false);
    }
  };

  const startEditTask = (task) => {
    if (editingTaskId === task.id) {
      setEditingTaskId(null);
      setEditedTask({ instruction: '', is_report_required: true, status: 'pending', skipping_reason: '' });
      return;
    }

    setEditingTaskId(task.id);
    setEditedTask({
      instruction: task.instruction || '',
      is_report_required: !!task.is_report_required,
      status: task.status || 'pending',
      skipping_reason: task.skipping_reason || '',
    });
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditedTask({ instruction: '', is_report_required: true, status: 'pending', skipping_reason: '' });
  };

  const deleteBookingTask = async (taskId) => {
    const ok = window.confirm('Are you sure you want to delete this booking task?');
    if (!ok) return;

    setIsProcessing(true);
    setFeedbackMessage('');

    try {
      await axios.delete(`/api/tasks/booking/${bookingId}/${taskId}`);
      await fetchBookingTasks();
      setFeedbackMessage('Booking task deleted.');
      cancelEditTask();
    } catch (e) {
      console.error('Error deleting booking task:', e);
      setFeedbackMessage(e.response?.data?.error || 'Failed to delete booking task.');
    } finally {
      setIsProcessing(false);
    }
  };

  const openReportModal = (task) => {
    if (!task?.id) return;
    setReportTask(task);
    setReportModalOpen(true);
  };

  // View skip reason (for skipped tasks)
  const openSkipReasonModal = async (task) => {
    if (!task?.id) return;
    setSkipTask(task);
    setSkipReason('');
    setSkipModalOpen(true);

    const fromRow = task?.skipping_reason || task?.skip_reason || task?.reason || '';
    if (fromRow) {
      setSkipReason(String(fromRow));
      return;
    }

    setSkipLoading(true);
    try {
      setSkipReason('No Reason Given for Skipping');
    } catch (e) {
      console.error('Failed to load skip reason:', e);
      setSkipReason('Skipping reason not available.');
    } finally {
      setSkipLoading(false);
    }
  };

  // ✅ Save button handler: saves instruction/report_required (booking source) AND status (+ skipping_reason) in one click.
  const saveEdits = async (taskRow) => {
    if (!bookingId || !taskRow?.id) return;

    const taskId = taskRow.id;
    const isBooking = taskRow.source === 'booking';

    const instruction = (editedTask.instruction || '').trim();
    if (isBooking && !instruction) {
      setFeedbackMessage('Please enter a task instruction.');
      return;
    }

    const nextStatus = String(editedTask.status || 'pending').toLowerCase();

    // ✅ If skipping, reason is mandatory
    if (nextStatus === 'skipped') {
      const reason = (editedTask.skipping_reason || '').trim();
      if (!reason) {
        setFeedbackMessage('Skipping reason is required when status is Skipped.');
        return;
      }
    }

    setIsProcessing(true);
    setFeedbackMessage('');

    try {
      if (isBooking) {
        // ✅ single route for booking tasks: updates instruction + report_required + status + skipping_reason
        const payload = {
          instruction,
          is_report_required: !!editedTask.is_report_required,
          status: nextStatus,
          skipping_reason: nextStatus === 'skipped' ? (editedTask.skipping_reason || '').trim() : null,
        };
        await axios.put(`/api/tasks/booking/${bookingId}/${taskId}`, payload);
      } else {
        // ✅ status-only route for client/property tasks: updates status + skipping_reason
        const payload = {
          status: nextStatus,
          skipping_reason: nextStatus === 'skipped' ? (editedTask.skipping_reason || '').trim() : null,
        };
        await axios.put(`/api/tasks/booking/${bookingId}/${taskId}/status`, payload);
      }

      await fetchBookingTasks();
      setEditingTaskId(null);
      setEditedTask({ instruction: '', is_report_required: true, status: 'pending', skipping_reason: '' });
      setFeedbackMessage('Task updated.');
    } catch (e) {
      console.error('Error saving task:', e);
      setFeedbackMessage(e.response?.data?.error || 'Failed to save task.');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * ✅ ONE clickable colored label logic
   */
  const getMainStatusChip = (t) => {
    const status = String(t?.status || 'pending').toLowerCase();
    const reportRequired = !!t?.is_report_required;
    const hasReport = !!t?.has_report || Number(t?.report_count || 0) > 0;

    let label = 'Pending';
    if (status === 'skipped') label = 'Skipped';
    else if (status === 'completed') label = hasReport ? 'Completed with Report' : 'Completed';
    else if (status === 'pending' && hasReport) label = 'Waiting Admin Approval';
    else label = 'Pending';

    let clickable = false;
    let onClick = null;
    let variant = 'pending';

    if (status === 'skipped') {
      clickable = true;
      onClick = () => openSkipReasonModal(t);
      variant = 'skipped';
    } else if (status === 'completed') {
      if (hasReport) {
        clickable = true;
        onClick = () => openReportModal(t);
        variant = 'completed';
      } else {
        clickable = false;
        variant = 'completed';
      }
    } else {
      if (hasReport) {
        clickable = true;
        onClick = () => openReportModal(t);
        variant = 'waiting';
      } else if (reportRequired) {
        clickable = true;
        onClick = () => openReportModal(t);
        // Button-style action chip — the leading + icon comes from the JSX.
        label = 'Upload Report';
        variant = 'upload';
      } else {
        clickable = false;
        variant = 'pending';
      }
    }

    return { label, clickable, onClick, variant };
  };

  const getMainChipClass = (variant) => {
    if (variant === 'waiting') return 'task-main-chip-waiting';
    if (variant === 'completed') return 'task-main-chip-completed';
    if (variant === 'skipped') return 'task-main-chip-skipped';
    // Upload is an ACTION, not a status — it gets a real button look so
    // admins immediately see it's clickable.
    if (variant === 'upload') return 'task-main-chip-upload';
    return 'task-main-chip-pending';
  };

  // Creation order (id ASC) — the displayed number must match what the client
  // and cleaner see for this booking, so "task 3" means the same task everywhere.
  const sortedTasks = useMemo(
    () => [...tasks].sort((a, b) => (a.id || 0) - (b.id || 0)),
    [tasks]
  );

  if (initialLoading) {
    /* The card shell renders immediately and the loading happens INSIDE it
       (owner, 2026-09-02) — a bare page-centre spinner shifted the whole
       layout when the content arrived. */
    return (
      <div className="dp-card booking-section full-width">
        <div className="client-tasks-title-wrap dp-cardHead">
          <div className="client-tasks-title-block">
            <h3 className="client-tasks-title">Tasks</h3>
          </div>
        </div>
        <SkeletonRows rows={3} />
      </div>
    );
  }

  return (
    <div className="dp-card booking-section full-width">
      {/* Header */}
      <div className="client-tasks-title-wrap dp-cardHead">
        <div className="client-tasks-title-block">
          <h3 className="client-tasks-title">Tasks</h3>
          <div className="client-tasks-subtitle">
            These tasks are copied from the Client + Property, and you can add Booking-specific tasks here.
          </div>
        </div>

        {(() => {
          const total = tasks.length;
          const completed = tasks.filter((t) => String(t.status || '').toLowerCase() === 'completed').length;
          const allDone = total > 0 && completed === total;
          if (total === 0) return null;

          return (
            <span className={`client-tasks-count ${allDone ? 'client-tasks-count-done' : 'client-tasks-count-notdone'}`}>
              Completed Tasks: {completed} / {total}
            </span>
          );
        })()}
      </div>

      {/* Add Booking Task */}
      <div className="client-tasks-add">
        <div className="client-tasks-add-row">
          <input
            className="client-tasks-input"
            type="text"
            placeholder="Add task to this booking"
            value={newInstruction}
            onChange={(e) => setNewInstruction(e.target.value)}
            maxLength={180}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addBookingTask();
              }
            }}
            disabled={isProcessing}
          />

          <label className="client-tasks-checkbox">
            <input
              type="checkbox"
              checked={newReportRequired}
              onChange={(e) => setNewReportRequired(e.target.checked)}
              disabled={isProcessing}
            />
            Report Required
          </label>

          <button className="client-tasks-add-btn" onClick={addBookingTask} disabled={isProcessing} title="Add Booking Task">
            <FontAwesomeIcon icon={faPlus} />
            <span>Add</span>
          </button>
        </div>
      </div>

      {/* List */}
      {tasks.length > 0 ? (
        <div className="client-tasks-list" style={{ marginTop: 12 }}>
          {sortedTasks.map((t, index) => {
            const isBooking = t.source === 'booking';
            const isEditing = editingTaskId === t.id;

            const hasReport = !!t.has_report || Number(t.report_count || 0) > 0;
            const reportRequired = !!t.is_report_required;

            const mainChip = getMainStatusChip(t);

            // Grey label text for report requirement (NOT clickable)
            const reportReqLabel = reportRequired ? (hasReport ? 'Report Added' : 'Report Required') : 'No Report Required';

            return (
              <div className="client-task-card" key={t.id}>
                <div className="client-task-row">
                  <div className="client-task-left">
                    <span className="client-task-number">{index + 1}.</span>

                    {/* Setup-photo instruction thumbnail (photo-derived tasks) */}
                    {t.photo_url && (
                      <button
                        type="button"
                        className="client-task-photo"
                        title="Click to view the setup photo for this task"
                        onClick={() => setPhotoModalUrl(t.photo_url)}
                      >
                        <img src={t.photo_url} alt="Task setup" />
                      </button>
                    )}

                    {/* instruction editable ONLY for booking source */}
                    {isEditing && isBooking ? (
                      <input
                        className="client-tasks-input"
                        type="text"
                        value={editedTask.instruction}
                        onChange={(e) => setEditedTask((p) => ({ ...p, instruction: e.target.value }))}
                        maxLength={180}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveEdits(t);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEditTask();
                          }
                        }}
                        style={{ height: 36 }}
                        disabled={isProcessing}
                      />
                    ) : (
                      <div className="client-task-instruction">{t.instruction}</div>
                    )}
                  </div>

                  <div className="client-task-right">
                    {/* Grey 1: Source badge */}
                    <button
                      type="button"
                      className={`client-task-badge client-task-source-badge client-task-source-${t.source || 'other'}`}
                      onClick={() => openSourceLink(t.source)}
                      disabled={t.source === 'booking'}
                      title={
                        t.source === 'client'
                          ? 'Open Client page'
                          : t.source === 'property'
                            ? 'Open Property page'
                            : 'Booking task'
                      }
                    >
                      {getSourceLabel(t.source)}
                    </button>

                    {/* Grey 2: Report Required / No Report Required */}
                    <span
                      className="client-task-badge client-task-badge-optional"
                      title={reportRequired ? 'Report required for this task' : 'No report required for this task'}
                      style={{
                        background: '#F3F4F6',
                        borderColor: '#E5E7EB',
                        color: '#374151',
                        cursor: 'default',
                      }}
                    >
                      {reportReqLabel}
                    </span>

                    {/* ✅ MAIN (colored) label or edit controls */}
                    {isEditing ? (
                      <>
                        <div style={{ width: 190 }}>
                          <Select
                            inputId={`task-status-${t.id}`}
                            isDisabled={isProcessing}
                            isSearchable={false}
                            styles={selectStyles}
                            options={statusOptions}
                            value={statusOptions.find((o) => o.value === String(editedTask.status || 'pending'))}
                            onChange={(opt) => {
                              const next = opt?.value || 'pending';
                              setEditedTask((p) => ({
                                ...p,
                                status: next,
                                skipping_reason: next === 'skipped' ? p.skipping_reason : '',
                              }));
                            }}
                            placeholder="Status"

                            // ✅ IMPORTANT:
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuPlacement="auto"
                          />
                        </div>

                        {/* ✅ mandatory reason field when skipped */}
                        {String(editedTask.status).toLowerCase() === 'skipped' && (
                          <input
                            className="client-tasks-input"
                            type="text"
                            placeholder="Skipping reason (required)"
                            value={editedTask.skipping_reason}
                            onChange={(e) => setEditedTask((p) => ({ ...p, skipping_reason: e.target.value }))}
                            maxLength={220}
                            style={{ height: 36, width: 260 }}
                            disabled={isProcessing}
                          />
                        )}
                      </>
                    ) : mainChip.clickable ? (
                      <button
                        type="button"
                        className={`client-task-badge task-main-chip task-main-chip-clickable ${getMainChipClass(mainChip.variant)}`}
                        onClick={mainChip.onClick}
                        disabled={isProcessing}
                        title={
                          mainChip.variant === 'skipped'
                            ? 'Click to view the skipping reason'
                            : mainChip.variant === 'upload'
                              ? 'Click to upload a report'
                              : 'Click to view the report'
                        }
                      >
                        <FontAwesomeIcon
                          icon={mainChip.variant === 'upload' ? faPlus : faEye}
                          className="task-chip-view-icon"
                          aria-hidden="true"
                        />
                        {mainChip.label}
                      </button>
                    ) : (
                      <span className={`client-task-badge task-main-chip ${getMainChipClass(mainChip.variant)}`}>
                        {mainChip.label}
                      </span>
                    )}

                    {/* Icons */}
                    {isEditing ? (
                      <>
                        {/* ✅ DELETE icon: ONLY visible while editing, and to the LEFT of pencil position */}
                        {isBooking && (
                          <button
                            className="client-task-delete-btn"
                            onClick={() => !isProcessing && deleteBookingTask(t.id)}
                            title="Delete booking task"
                            type="button"
                            style={{ marginLeft: 6 }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}

                        <FontAwesomeIcon
                          icon={faCheck}
                          className="user-edit-icon user-save-icon"
                          title="Save"
                          onClick={() => {
                            if (isProcessing) return;
                            saveEdits(t);
                          }}
                        />
                        <FontAwesomeIcon
                          icon={faTimes}
                          className="user-edit-icon user-cancel-icon"
                          title="Cancel"
                          onClick={() => !isProcessing && cancelEditTask()}
                        />
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faPen}
                          className="user-edit-icon edit-user-icon"
                          title={isBooking ? 'Edit task' : 'Edit status'}
                          onClick={() => !isProcessing && startEditTask(t)}
                        />
                        {/* ✅ delete removed from non-editing mode */}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="client-tasks-empty" style={{ marginTop: 12 }}>
          No tasks yet.
        </div>
      )}

      {!!feedbackMessage && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
          {feedbackMessage}
        </div>
      )}

      {/* Report modal */}
      {reportModalOpen && reportTask && (
        <TaskReportModal
          bookingId={bookingId}
          task={reportTask}
          onClose={() => {
            setReportModalOpen(false);
            setReportTask(null);
          }}
          onUploaded={async () => {
            await fetchBookingTasks();
          }}
        />
      )}

      {/* Skipping reason modal (view only) */}
      {skipModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Skipping Reason</h3>
              <button
                type="button"
                className="client-tasks-add-btn"
                onClick={() => {
                  setSkipModalOpen(false);
                  setSkipTask(null);
                  setSkipReason('');
                }}
                disabled={skipLoading}
              >
                Close
              </button>
            </div>

            <div style={{ marginTop: 12, fontSize: 14, color: '#111827' }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Task</div>
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: 12 }}>
                {skipTask?.instruction || '—'}
              </div>

              <div style={{ fontWeight: 700, marginTop: 14, marginBottom: 6 }}>Reason</div>
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: 12 }}>
                {skipLoading ? 'Loading...' : (skipReason || '—')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task setup-photo lightbox (in-page, not a new tab) */}
      {photoModalUrl && (
        <div className="task-photo-modal" onClick={() => setPhotoModalUrl(null)}>
          <button
            type="button"
            className="task-photo-modal-x"
            onClick={(e) => { e.stopPropagation(); setPhotoModalUrl(null); }}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={photoModalUrl}
            alt="Task setup"
            className="task-photo-modal-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}